import type { ReactNode } from "react";

import { Icon, Text } from "@alpacahq/ui/old";

import { NewWindowLink } from "~/v2/components/common";

const SUPPORTED_REGIONS_LINK =
  "https://alpaca.markets/support/countries-alpaca-is-available";

const SIPC_PROTECTIONS_BLOG_LINK =
  "https://alpaca.markets/blog/alpaca-clearing-expands-excess-sipc-coverage-strengthening-commitment-towards-enhanced-protection/";

interface CheckItemProps {
  children: ReactNode;
}

const CheckItem = ({ children }: CheckItemProps) => (
  <li className="flex items-center justify-start gap-2.5">
    <Icon name="CheckBadge" variant="filled" className="text-yellow-400" />
    <Text className="text-sm">{children}</Text>
  </li>
);

export const NoSelectionLegalText = () => (
  <div className="flex flex-col gap-4">
    <Text className="font-medium text-lg">Accounts at Alpaca</Text>
    <ul className="flex flex-col gap-2">
      <CheckItem>
        Individual accounts are designed for individual Investors.
      </CheckItem>
      <CheckItem>
        Business accounts are designed to support LLCs, Corporations and
        Partnerships.
      </CheckItem>
    </ul>
    <Text className="font-semibold">Supported Regions</Text>
    <ul className="flex flex-col gap-2">
      <CheckItem>
        We serve 196 countries and regions. See the{" "}
        <NewWindowLink
          className="inline"
          to={SUPPORTED_REGIONS_LINK}
          hasIcon={false}
        >
          full list
        </NewWindowLink>
        .
      </CheckItem>
    </ul>
    <Text className="font-semibold">Enhanced SIPC Coverage</Text>
    <ul className="flex flex-col gap-2">
      <CheckItem>
        Your assets are protected up to $500,000 through SIPC, in addition to
        Excess SIPC Coverage. For more details, review our{" "}
        <NewWindowLink
          className="inline"
          to={SIPC_PROTECTIONS_BLOG_LINK}
          hasIcon={false}
        >
          SIPC and Excess SIPC Protection
        </NewWindowLink>
        .
      </CheckItem>
    </ul>
    <Text className="font-semibold">Rigorous Security</Text>
    <ul className="flex flex-col gap-2">
      <CheckItem>
        We uphold the highest security standards, undergoing SOC 2 Type 2 audits
        to ensure the safety of your personal information at all times.
      </CheckItem>
    </ul>
  </div>
);

export const EntityLegalText = () => (
  <div className="flex flex-col gap-4">
    <Text className="font-medium text-lg">Business Account</Text>
    <Text className="text-sm">
      Designed to support business structures such as LLCs, Corporations and
      Partnerships.
    </Text>
    <Text className="text-sm">
      Other business types may require additional documentation, review and
      subject to approval.
    </Text>
    <Text className="text-sm">
      An authorized representative of your company must complete and sign the
      application.
    </Text>
    <Text className="text-sm">
      You will need the following items for the application:
    </Text>
    <ul className="flex flex-col gap-2">
      <CheckItem>Business Registration Document</CheckItem>
      <CheckItem>Company Formation Document</CheckItem>
      <CheckItem>
        Business Operating Document (e.g. Bylaws, Operating Agreement, Charter)
      </CheckItem>
      <CheckItem>
        Tax ID information for the business, Authorized Individuals, and
        Ultimate Beneficial Owners.
      </CheckItem>
      <CheckItem>
        Valid, government-issued ID for all Authorized Individuals.
      </CheckItem>
      <CheckItem>
        Valid, government-issued ID for every Ultimate Beneficial Owner holding
        10% or more ownership of the business
      </CheckItem>
    </ul>
  </div>
);

export const IndividualLegalText = () => (
  <div className="flex flex-col gap-4">
    <Text className="font-medium text-lg">Individual Account</Text>
    <Text className="text-sm">
      Designed for individual investors. To apply, the account holder will need:
    </Text>
    <ul className="flex flex-col gap-2">
      <CheckItem>A valid, government-issued ID</CheckItem>
      <CheckItem>Personal information </CheckItem>
      <CheckItem>
        A document to confirm your address (e.g. recent utility bill or bank
        statement)
      </CheckItem>
    </ul>
    <Text className="text-sm">
      You may be asked to provide additional documents after submission.
    </Text>
  </div>
);
